import React, { useEffect, useState } from "react"
import Popup from "component/popup/popup"
import InputText from "component/inputText/inputText"
import Select from "component/select/select"
import Toggle from "component/toggle/Toggle"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import SecretField from "./secretField"
import {
    buildCreateUserPayload,
    CONFIGURATION_ROLE_OPTIONS,
    ConfigurationUserRoleName,
    ConfigurationUsernameType,
    identifierError,
    nameError,
    passwordError,
    sanitizePhone,
} from "./configurationUsers"
import styles from "./configuration.module.scss"

type CreateUserPopupProps = {
    isOpen: boolean
    onClose: () => void
    onSuccess: () => void
}

const CreateUserPopup: React.FC<CreateUserPopupProps> = ({ isOpen, onClose, onSuccess }) => {
    const { showSuccess, showError } = useSnackbar()
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [usernameType, setUsernameType] = useState<ConfigurationUsernameType>("email")
    const [email, setEmail] = useState("")
    const [phone, setPhone] = useState("")
    const [password, setPassword] = useState("")
    const [roleName, setRoleName] = useState("")
    const [flagActive, setFlagActive] = useState(true)
    const [errors, setErrors] = useState<Record<string, string | undefined>>({})
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setFirstName("")
        setLastName("")
        setUsernameType("email")
        setEmail("")
        setPhone("")
        setPassword("")
        setRoleName("")
        setFlagActive(true)
        setErrors({})
    }, [isOpen])

    const switchType = (next: ConfigurationUsernameType) => {
        setUsernameType(next)
        setEmail("")
        setPhone("")
        setErrors((prev) => ({ ...prev, identifier: undefined }))
    }

    const handleCreate = async () => {
        const nextErrors: Record<string, string | undefined> = {
            firstName: nameError(firstName, "First name"),
            identifier: identifierError(usernameType, email, phone),
            password: passwordError(password),
            role: roleName ? undefined : "Role is required",
        }
        setErrors(nextErrors)
        if (Object.values(nextErrors).some(Boolean)) return

        setIsSaving(true)
        try {
            const response = await agent.CreateUserAPI.post(
                buildCreateUserPayload({
                    firstName,
                    lastName,
                    usernameType,
                    email,
                    phone,
                    password,
                    roleName: roleName as ConfigurationUserRoleName,
                    flagActive,
                }),
            )
            if (response?.status === true) {
                showSuccess("User created")
                onSuccess()
                onClose()
            } else {
                showError(response?.error?.message || "Failed to create user")
            }
        } catch (err) {
            console.error("Create user error", err)
            showError("Failed to create user")
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title="Create New User"
            size="medium"
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            primaryButton={{ label: isSaving ? "Creating..." : "Create User", onClick: handleCreate, disabled: isSaving }}
            showCloseButton={false}
        >
            <div className={styles.formStack}>
                <div className={styles.formRow}>
                    <InputText
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="Enter first name"
                        error={errors.firstName}
                    />
                    <InputText
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Enter last name"
                        error={errors.lastName}
                    />
                </div>
                <div>
                    <span className={styles.typeLabel}>Username Type</span>
                    <div className={styles.typeToggle}>
                        <button
                            type="button"
                            className={`${styles.typeBtn} ${usernameType === "email" ? styles.typeBtnActive : ""}`}
                            onClick={() => switchType("email")}
                        >
                            Email Address
                        </button>
                        <button
                            type="button"
                            className={`${styles.typeBtn} ${usernameType === "phone" ? styles.typeBtnActive : ""}`}
                            onClick={() => switchType("phone")}
                        >
                            Phone Number
                        </button>
                    </div>
                </div>
                {usernameType === "email" ? (
                    <InputText
                        label="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="example@traveladvocates.com"
                        type="email"
                        error={errors.identifier}
                    />
                ) : (
                    <InputText
                        label="Phone Number"
                        value={phone}
                        onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                        placeholder="Enter 10 digit phone number"
                        type="tel"
                        maxLength={10}
                        error={errors.identifier}
                    />
                )}
                <SecretField
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    name="new-user-password"
                    autoComplete="new-password"
                    error={errors.password}
                />
                <Select
                    label="Assigned Role"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    options={CONFIGURATION_ROLE_OPTIONS}
                    placeholder="Select role"
                    error={errors.role}
                />
                <div className={styles.statusRow}>
                    <div className={styles.statusCopy}>
                        <span className={styles.statusTitle}>Set Status Active</span>
                        <span className={styles.statusHint}>New user will be able to log in immediately</span>
                    </div>
                    <Toggle checked={flagActive} onChange={setFlagActive} />
                </div>
            </div>
        </Popup>
    )
}

export default CreateUserPopup
